/**
 * v0.4 TD-21 (2026-09-03): 開いているモーダルのうち「どれが最前面か」を追跡する。
 *
 * ⚠️ 従来 `components/Modal.tsx` は開いているインスタンスごとに独立した document レベルの
 * keydown リスナーを登録するだけで最前面を追跡していなかった。そのため入れ子モーダル
 * (編集モーダルの中に確認ダイアログ) で Escape を押すと**登録順に両方のリスナーが発火し、
 * 内側だけでなく外側の編集モーダルごと閉じてしまう** (期待される「1段階だけ戻る」にならない)。
 * Tab のフォーカストラップも同様に最前面へスコープされていなかった。
 *
 * ⚠️ 「後から登録された方が最前面」という単純な順序では不十分:
 * React の useEffect は子から親の順に走るため、外側と内側が同じコミットで開く場合に
 * 内側が先に登録され、順序だけで判定すると**外側が最前面と誤判定される**。
 * そこで DOM の包含関係を第一の根拠にし (自分の中に別の開いているモーダルがあれば自分は下)、
 * 包含関係が無いもの同士 (兄弟として並ぶモーダル) だけ登録順で決める。
 *
 * ⚠️⚠️ **前提: z-index は考慮しない。** 判定材料は DOM の包含関係と登録順だけなので、
 * ①入れ子 (外側モーダルの children に確認ダイアログを置く) か
 * ②兄弟配置なら、視覚的に手前に出したい方を**後に宣言する**
 * のどちらかを守ること。`zIndexClass` で手前に出したモーダルを先に宣言すると、
 * 見た目と Escape の対象が食い違う。
 * (実測では `ShiftBoardPage`/`HierarchyPage` の `z-[60]` はいずれも DOM 入れ子のため一致。
 *  ConfirmDialog を Modal より前に宣言している画面が3つあるが、同時に開かないため現状は無害)
 */

/** ⚠️ 素直に `HTMLElement` を使う (本番コードからキャストを消すため)。
 *  web のテストは Node 環境で DOM を持たないため、テスト側で疑似要素を
 *  `as unknown as HTMLElement` して渡す (キャストはテストに閉じ込める)。 */
export type ModalStackEntry = HTMLElement;

const stack: ModalStackEntry[] = [];

export const registerModal = (entry: ModalStackEntry): void => {
  if (!stack.includes(entry)) stack.push(entry);
};

export const unregisterModal = (entry: ModalStackEntry): void => {
  const i = stack.indexOf(entry);
  if (i >= 0) stack.splice(i, 1);
};

/** 現在の最前面。開いているモーダルが無ければ null。 */
export const resolveTopmostModal = (): ModalStackEntry | null => {
  // 自分の内側に別の開いているモーダルを含むものは最前面ではない
  const leaves = stack.filter((el) => !stack.some((o) => o !== el && el.contains(o)));
  return leaves.length > 0 ? leaves[leaves.length - 1]! : null;
};

export const isTopmostModal = (entry: ModalStackEntry): boolean => resolveTopmostModal() === entry;

/** テスト専用: モジュールレベルの状態をリセットする。 */
export const __resetModalStackForTest = (): void => { stack.length = 0; };
